// src/components/BusinessHome.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./businessHome.css";
import { supabase } from "../../supabase_client.jsx";
import colaImage from "../../assets/columbia.jpg";

export default function BusinessHome({ currentUser, onViewSelect, onProfileSetupComplete }) {
  const handleProfileSetupComplete = () => {
    setIsProfileComplete(true);
    if (onProfileSetupComplete) onProfileSetupComplete();
  };

  const [username, setUsername] = useState("");
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [kpis, setKpis] = useState({
    totalEvents: 0,
    upcomingEvents: 0,
    totalRSVPs: 0,
    totalBookmarks: 0,
  });

  const [animatedKpis, setAnimatedKpis] = useState({
    totalEvents: 0,
    upcomingEvents: 0,
    totalRSVPs: 0,
    totalBookmarks: 0,
  });

  const userId = currentUser?.user?.id;

  const [logoUrl, setLogoUrl] = useState("");
  const [bannerUrl, setBannerUrl] = useState("");
  const [isProfileComplete, setIsProfileComplete] = useState(true);

  const initials = useMemo(() => {
    if (!username) return "B";
    return username.trim().split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  }, [username]);

  useEffect(() => {
    if (!userId) return;

    const fetchData = async () => {

      const { data: userData } = await supabase
        .from("users")
        .select("username")
        .eq("user_id", userId)
        .single();

      const fetchedUsername = userData?.username;
      if (!fetchedUsername) return;
      setUsername(fetchedUsername);

      const { data: bizData } = await supabase
      .from("businesses")
      .select("logo_url, name, banner_url, has_setup_profile")
      .eq("user_id", userId)
      .single();

      if (bizData) {
        setLogoUrl(bizData.logo_url || "");
        setBannerUrl(bizData.banner_url || "");
        setIsProfileComplete(bizData.has_setup_profile === true); 
      }

      const { data: eventData } = await supabase
        .from("events")
        .select("*")
        .eq("event_owner", fetchedUsername);

      if (!eventData) return;

      setAllEvents(eventData);

      const today = new Date();
      const upcoming = eventData.filter(
        (event) => new Date(event.date) >= today
      );

      const nextWeek = new Date();
      nextWeek.setDate(today.getDate() + 7);

      const upcomingDisplay = eventData
        .filter((event) => {
          const eventDate = new Date(event.date);
          return eventDate >= today && eventDate <= nextWeek;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      setEvents(upcomingDisplay);

      const eventIds = eventData.map((e) => e.id);

      const { data: rsvpData } = await supabase
        .from("marked_yes_events")
        .select("event_id")
        .in("event_id", eventIds);

      const { data: bookmarkData } = await supabase
        .from("bookmarked_events")
        .select("event_id")
        .in("event_id", eventIds);

      setKpis({
        totalEvents: eventData.length,
        upcomingEvents: upcoming.length,
        totalRSVPs: rsvpData?.length || 0,
        totalBookmarks: bookmarkData?.length || 0,
      });
    };

    

    fetchData();
  }, [userId]);

  useEffect(() => {
    const duration = 800;
    const steps = 30;
    const interval = duration / steps;

    const counters = Object.keys(kpis);

    counters.forEach((key) => {
      let start = 0;
      const end = kpis[key];
      const increment = end / steps;

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          start = end;
          clearInterval(timer);
        }
        setAnimatedKpis((prev) => ({
          ...prev,
          [key]: Math.floor(start),
        }));
      }, interval);
    });
  }, [kpis]);

  return (
    <div className="home-container">
      <div className="home-card">
        <div className="app-title-banner">
            <h1 className="app-title-text">Do You Wanna Go?</h1>
        </div>
      <div className="business-home">

        {/* header */}
        <div className="calendar-top-nav">
            <div className="nav-left">
                <button className="nav-tab active">General</button>
                <button className="nav-tab" onClick={() => onViewSelect('business')}>Calendar</button>
                <button className="nav-tab" onClick={() => onViewSelect('businessCRMDash')}>Dashboard</button>
                <button className="nav-tab" onClick={() => onViewSelect('event-management')}>Event Management</button>
            </div>
            <div className="nav-right">
              <div className="nav-avatar-circle" style={{ position: "relative" }} onClick={() => onViewSelect("business-profile")}>
                {logoUrl
                  ? <img src={logoUrl} alt="profile" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
                  : initials
                }
                {!isProfileComplete && <span className="avatar-dot" />}
              </div>
            </div>
        </div>

        <div className="overlap-section">
            <img src={bannerUrl || colaImage} alt="Columbia" className="detail-image" />
          <div className="welcome-card">
            <h1 className="home-title">Welcome Back, {username || "..."}!</h1>
            <p className="welcome-subtitle">Here's what's happening with your business today.</p>
          </div>
        </div>

        {/* HEADER */}
        <div className="dashboard-header">
          <div>
            <h1>Here's your business snapshot.</h1>
          </div>
          <button
            className="primary-btn"
            onClick={() => onViewSelect("event-management")}
          >
            + Create Event
          </button>
        </div>

        <div className="kpi-grid">
          <div className="kpi-cards">
            <h3>{animatedKpis.totalEvents}</h3>
            <p>Total Events</p>
          </div>
          <div className="kpi-cards">
            <h3>{animatedKpis.upcomingEvents}</h3>
            <p>Upcoming Events</p>
          </div>
          <div className="kpi-cards">
            <h3>{animatedKpis.totalRSVPs}</h3>
            <p>Total RSVPs</p>
          </div>
          <div className="kpi-cards">
            <h3>{animatedKpis.totalBookmarks}</h3>
            <p>Bookmarks</p>
          </div>
        </div>

        <div className="section-card">
          <div className="section-header">
            <h2>Upcoming Events</h2>
            <button
              className="link-btn"
              onClick={() => onViewSelect("business")}
            >
              View Calendar →
            </button>
          </div>

          <div className="event-list">
            {events.length === 0 ? (
              <p className="empty-state">No upcoming events this week.</p>
            ) : (
              events.slice(0, 5).map((event) => (
                <div key={event.id} className="event-item no-click">
                  <div className="event-text">
                    <span className="event-name">{event.name}</span>
                    <span className="event-date">
                      {new Date(event.date).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="quick-actions">
          <div className="action-card" onClick={() => onViewSelect("business")}>
            Calendar
          </div>
          <div className="action-card" onClick={() => onViewSelect("businessCRMDash")}>
            Dashboard
          </div>
          <div className="action-card" onClick={() => onViewSelect("event-management")}>
            Manage Events
          </div>
        </div>
      </div>
    </div>
  </div>
  );
}